import { Component, computed, inject, Input, Signal } from '@angular/core';
import { ComponentsModule } from '@mastechnicaegroup/carelinelive-ui';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { ClientSingleResponse } from '@lib/api/models/client';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Package } from '@lib/api/models/package';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { PackageService } from '@lib/package/package.service';
import { map, combineLatest, distinctUntilChanged, switchMap, shareReplay } from 'rxjs';
import { PaginationCardComponent } from "@shared/pagination/pagination-card.component";

@Component({
    selector: 'app-client-packages-tab',
    imports: [
    ComponentsModule,
    RouterLink,
    PaginationCardComponent,
    DatePipe
],
    templateUrl: './client-packages-tab.component.html',
    standalone: true,
    styleUrl: './client-packages-tab.component.scss'
})
export class ClientPackagesTabComponent {
    @Input() clientData: ClientSingleResponse | undefined;
    private readonly _activeRoute = inject(ActivatedRoute);
    private readonly _packageService = inject(PackageService);
    private readonly _router = inject(Router);

    id = this._activeRoute.snapshot.paramMap.get('id');

    protected readonly page = toSignal<number>(this._activeRoute.queryParams.pipe(
        map((params): number => params['page'] ?? 1)
    ));

    protected readonly pageResult: Signal<Result<PaginatedApiResponse<Package>> | undefined> = toSignal(combineLatest({    
        page: toObservable(this.page),
        }).pipe(
            distinctUntilChanged((prev, cur) => {
                return JSON.stringify(prev) === JSON.stringify(cur);
            }),
            switchMap(({ page }) => {
                return this._packageService.listClientPackages(page, this.id!).pipe(
                    toResult()
                );
            }),

            shareReplay(1)
    ));

    protected readonly meta = computed(() => {
        return this.pageResult()?.value?.meta;
    });

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }
}
